import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { mediaUrl } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Should this visitor see the pop-up now?  frequency: always | visit | daily | once
 * (legacy promos without `frequency` fall back to show_once_per_session).
 */
export const shouldShowPromo = (promo, storage = { session: window.sessionStorage, local: window.localStorage }) => {
  const freq = promo.frequency || (promo.show_once_per_session === false ? 'always' : 'visit');
  const key = `ba_promo_seen_${promo.id}`;
  if (freq === 'always') return true;
  if (freq === 'visit') return !storage.session.getItem(key);
  const seenAt = Number(storage.local.getItem(key) || 0);
  if (freq === 'daily') return !seenAt || Date.now() - seenAt > DAY_MS;
  if (freq === 'once') return !seenAt;
  return true;
};

export const markPromoSeen = (promo, storage = { session: window.sessionStorage, local: window.localStorage }) => {
  const key = `ba_promo_seen_${promo.id}`;
  storage.session.setItem(key, '1');
  storage.local.setItem(key, String(Date.now()));
};

/** The pop-up itself - shared by the public site and the admin "Preview" so both look identical. */
export const PromoDialog = ({ promo, open, onOpenChange, reserveUrl = '', preview = false, testId = 'promo-popup' }) => {
  if (!promo) return null;
  const cta = promo.cta_url || reserveUrl || '#';
  const isOffer = promo.kind === 'offer';
  const stop = (e) => { if (preview) { e.preventDefault(); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 overflow-hidden bg-card sm:max-w-2xl border-0 sm:rounded-2xl max-h-[92vh] overflow-y-auto" data-testid={testId}>
        {preview && <span className="absolute top-3 left-3 z-10 rounded-full bg-black/60 text-white text-[10px] tracking-[0.18em] uppercase px-2.5 py-1" data-testid={`${testId}-preview-badge`}>Preview</span>}
        <div className={`grid ${promo.image ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
          {promo.image && (
            <div className="relative h-52 sm:h-auto sm:min-h-[420px] bg-muted">
              <img src={mediaUrl(promo.image)} alt={promo.title} className="absolute inset-0 h-full w-full object-cover" />
            </div>
          )}
          <div className="p-7 sm:p-9 flex flex-col">
            <p className="eyebrow text-[hsl(var(--brand-gold))]">{promo.subtitle || 'Special offer'}</p>
            <DialogTitle className="font-display text-2xl sm:text-3xl font-normal mt-3 leading-tight">{promo.title || 'Your pop-up title'}</DialogTitle>
            {promo.price_label && <p className="mt-2 text-base text-foreground/85" data-testid={`${testId}-price`}>{promo.price_label}</p>}
            <DialogDescription className="mt-4 text-[15px] leading-relaxed text-foreground/75 whitespace-pre-line line-clamp-6">{promo.body || 'Your message to guests appears here.'}</DialogDescription>
            {promo.end_date && <p className="mt-4 text-xs tracking-[0.12em] uppercase text-muted-foreground">Valid until {new Date(promo.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
              <a href={cta} onClick={stop} target="_blank" rel="noopener noreferrer" className="btn-gold whitespace-nowrap shrink-0" data-testid={`${testId}-cta`}>
                {promo.cta_label || 'Explore Now'} <ArrowUpRight className="h-4 w-4" />
              </a>
              {isOffer && (
                preview
                  ? <span className="text-[12px] tracking-[0.16em] uppercase text-muted-foreground py-2 whitespace-nowrap shrink-0">All offers</span>
                  : <Link to="/offers" onClick={() => onOpenChange(false)} className="text-[12px] tracking-[0.16em] uppercase text-muted-foreground hover:text-foreground py-2 whitespace-nowrap shrink-0" data-testid={`${testId}-all-offers`}>All offers</Link>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Website pop-up: shows a scheduled promo, or (when none is live) a Special Offer flagged "show in pop-up". */
export const PromoPopup = () => {
  const { site } = useSite();
  const promo = site?.promo;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!promo) return undefined;
    if (!shouldShowPromo(promo)) return undefined;
    const t = setTimeout(() => {
      setOpen(true);
      markPromoSeen(promo);
    }, promo.delay_ms ?? 1800);
    return () => clearTimeout(t);
  }, [promo]);

  if (!promo) return null;
  return <PromoDialog promo={promo} open={open} onOpenChange={setOpen} reserveUrl={site?.settings?.reserve_url} />;
};
